<template>
  <div v-if="isVisible" class="credit-history-modal-overlay" @click.self="closeModal">
    <div class="credit-history-modal">
      <div class="modal-header">
        <h2>{{ $t('credits.creditHistory') }}</h2>
        <button class="close-button" @click="closeModal" :aria-label="$t('common.close')">&times;</button>
      </div>

      <div class="modal-content">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>{{ $t('credits.loadingHistory') }}</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <p class="error-message">{{ error }}</p>
          <button @click="fetchHistory" class="btn btn-secondary">{{ $t('credits.retry') }}</button>
        </div>

        <!-- Empty State -->
        <div v-else-if="!loading && transactions.length === 0" class="empty-state">
          <div class="empty-icon">📋</div>
          <p>{{ $t('credits.noTransactionHistory') }}</p>
        </div>

        <!-- Transaction List -->
        <div v-else class="transactions-list">
          <div class="transaction-item" v-for="transaction in transactions" :key="transaction._id">
            <div class="transaction-main">
              <div class="transaction-type">
                <span class="type-icon" :class="getTypeClass(transaction.type)">
                  {{ getTypeIcon(transaction.type) }}
                </span>
                <div class="transaction-details">
                  <div class="transaction-description">{{ transaction.description || $t('credits.transaction') }}</div>
                  <div class="transaction-meta">
                    <span class="transaction-date">{{ formatDate(transaction.createdAt) }}</span>
                    <span v-if="transaction.service" class="transaction-service">{{ transaction.service }}</span>
                  </div>
                </div>
              </div>
              <div class="transaction-amount" :class="getAmountClass(transaction.amount)">
                {{ formatAmount(transaction.amount) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="pagination && pagination.total > pagination.limit" class="pagination">
          <button 
            @click="loadPrevious" 
            :disabled="!pagination.hasMore || currentOffset === 0"
            class="btn btn-secondary"
          >
            {{ $t('credits.previous') }}
          </button>
          <span class="pagination-info">
            {{ $t('credits.showing') }} {{ currentOffset + 1 }}-{{ Math.min(currentOffset + pagination.limit, pagination.total) }} {{ $t('credits.of') }} {{ pagination.total }}
          </span>
          <button 
            @click="loadNext" 
            :disabled="!pagination.hasMore"
            class="btn btn-secondary"
          >
            {{ $t('credits.next') }}
          </button>
        </div>
      </div>

      <div class="modal-footer">
        <button @click="closeModal" class="btn btn-primary">{{ $t('common.close') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted } from 'vue';
import { useCreditStore } from '../stores/credits';

export default {
  name: 'CreditHistoryModal',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const creditStore = useCreditStore();
    const loading = ref(false);
    const error = ref(null);
    const transactions = ref([]);
    const pagination = ref(null);
    const currentOffset = ref(0);
    const limit = 20;

    const fetchHistory = async (offset = 0) => {
      loading.value = true;
      error.value = null;
      currentOffset.value = offset;

      try {
        const result = await creditStore.fetchHistory({
          limit,
          offset,
          sortBy: 'createdAt',
          order: 'desc'
        });

        if (result) {
          transactions.value = result.transactions || [];
          pagination.value = result.pagination || null;
        }
      } catch (err) {
        console.error('Error fetching credit history:', err);
        error.value = err.message || 'Failed to load transaction history. Please try again.';
        transactions.value = [];
        pagination.value = null;
      } finally {
        loading.value = false;
      }
    };

    const loadNext = () => {
      if (pagination.value && pagination.value.hasMore) {
        fetchHistory(currentOffset.value + limit);
      }
    };

    const loadPrevious = () => {
      if (currentOffset.value > 0) {
        fetchHistory(Math.max(0, currentOffset.value - limit));
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'Unknown date';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const formatAmount = (amount) => {
      if (amount > 0) {
        return `+${amount}`;
      }
      return `${amount}`;
    };

    const getTypeIcon = (type) => {
      const icons = {
        credit: '➕',
        debit: '➖',
        bonus: '🎁',
        refund: '↩️',
        invitation: '✉️',
        referral: '👥'
      };
      return icons[type] || '💰';
    };

    const getTypeClass = (type) => {
      return `type-${type}`;
    };

    const getAmountClass = (amount) => {
      return amount > 0 ? 'amount-positive' : 'amount-negative';
    };

    const closeModal = () => {
      error.value = null;
      transactions.value = [];
      pagination.value = null;
      currentOffset.value = 0;
      emit('close');
    };

    // Watch for visibility changes to load history when modal opens
    watch(() => props.isVisible, (newVisible) => {
      if (newVisible) {
        fetchHistory(0);
      } else {
        // Reset state when modal closes
        error.value = null;
        transactions.value = [];
        pagination.value = null;
        currentOffset.value = 0;
      }
    });

    return {
      loading,
      error,
      transactions,
      pagination,
      currentOffset,
      fetchHistory,
      loadNext,
      loadPrevious,
      formatDate,
      formatAmount,
      getTypeIcon,
      getTypeClass,
      getAmountClass,
      closeModal
    };
  }
};
</script>

<style scoped>
.credit-history-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  animation: fadeIn 0.2s ease;
}

.credit-history-modal {
  background: var(--color-surface-elevated);
  border-radius: var(--radius-lg);
  width: 100%;
  max-width: 700px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: var(--gradient-hero);
  color: var(--color-primary-on);
  flex-shrink: 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
}

.close-button {
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-content {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 200px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: var(--color-text-muted);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.error-state {
  text-align: center;
  padding: 2rem;
}

.error-message {
  color: #c53030;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: var(--color-text-muted);
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.transactions-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.transaction-item {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 1rem;
  background: var(--color-surface-muted);
  transition: all 0.2s ease;
}

.transaction-item:hover {
  background: #edf2f7;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.transaction-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.transaction-type {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.type-icon {
  font-size: 1.5rem;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(2, 132, 199, 0.12);
}

.transaction-details {
  flex: 1;
  min-width: 0;
}

.transaction-description {
  font-weight: 500;
  color: var(--color-text);
  margin-bottom: 0.25rem;
  word-wrap: break-word;
}

.transaction-meta {
  display: flex;
  gap: 0.75rem;
  font-size: 0.85rem;
  color: var(--color-text-muted);
  flex-wrap: wrap;
}

.transaction-date {
  white-space: nowrap;
}

.transaction-service {
  padding: 0.125rem 0.5rem;
  background: var(--color-border);
  border-radius: 4px;
  font-size: 0.75rem;
}

.transaction-amount {
  font-size: 1.25rem;
  font-weight: 600;
  white-space: nowrap;
}

.amount-positive {
  color: #38a169;
}

.amount-negative {
  color: #e53e3e;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--color-border);
  gap: 1rem;
}

.pagination-info {
  color: var(--color-text-muted);
  font-size: 0.9rem;
  flex: 1;
  text-align: center;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--color-border);
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.2s ease;
}

.btn-primary {
  background: var(--gradient-hero);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(102, 126, 234, 0.3);
}

.btn-secondary {
  background: var(--color-border);
  color: var(--color-text-muted);
}

.btn-secondary:hover:not(:disabled) {
  background: #cbd5e0;
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .credit-history-modal {
    max-width: 95%;
    max-height: 90vh;
  }
  
  .modal-header {
    padding: 1rem;
  }
  
  .modal-content {
    padding: 1rem;
  }
  
  .modal-footer {
    padding: 1rem;
  }
  
  .transaction-main {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .transaction-amount {
    align-self: flex-end;
  }
  
  .pagination {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .pagination-info {
    order: -1;
  }
}
</style>
